/**
 * Custom hook for managing Provider Profile Update Form state and logic
 *
 * Extracts form state management, validation, and event handling
 * from the ProfileUpdateForm component for better separation of concerns.
 * Follows the same pattern as useProviderRegistrationFormState.
 *
 * @module hooks/useProviderProfileUpdateFormState
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { useProviderValidation } from '@/hooks/useProviderValidation';
import { scrollToFirstErrorField } from '@/utils/scrollToError';
import type { ProfileFormState } from '@/types/registration';
import { getLogger } from '@/services/shared/LoggerFactory';

// Define all form fields in order for validation and scrolling
const FORM_FIELDS = [
  'firstName',
  'lastName',
  'role',
  'providerNumber',
  'practiceName',
  'practiceAddress',
  'bio',
  'photo'
] as const;

/**
 * Hook configuration interface
 */
export interface UseProviderProfileUpdateFormStateConfig {
  providerId: string;
  initialData?: Partial<ProfileFormState['values']>;
  onSubmit: (data: ProfileFormState['values']) => Promise<void>;
  disabled?: boolean;
  isLoading?: boolean;
}

/**
 * Hook return interface
 */
export interface UseProviderProfileUpdateFormStateReturn {
  // Form state
  formData: ProfileFormState['values'];
  fieldRefs: React.RefObject<Record<string, HTMLInputElement | HTMLElement | null>>;

  // Form fields configuration
  FORM_FIELDS: readonly string[];

  // Form validation state
  fieldErrors: Record<string, string>;
  touchedFields: Set<string>;


  // Utility functions
  getFieldValue: (fieldName: string) => unknown;
  validateField: (fieldName: string, value?: unknown) => boolean;
  getFieldValidation: (fieldName: string) => {
    error?: string;
    warning?: string;
    success?: undefined;
  };
  getProviderNumberHelperText: () => string;

  // Event handlers
  handleFieldChange: (fieldName: string, value: string) => void;
  handlePhotoChange: (photo: File | undefined) => void;
  handleFieldBlur: (fieldName: string) => void;
  handleSubmit: (event: React.FormEvent) => Promise<void>;
}

/**
 * Custom hook for managing provider profile update form state and logic
 */
export const useProviderProfileUpdateFormState = ({
  providerId: _providerId, // eslint-disable-line @typescript-eslint/no-unused-vars
  initialData = {},
  onSubmit,
  disabled = false,
  isLoading = false,
}: UseProviderProfileUpdateFormStateConfig): UseProviderProfileUpdateFormStateReturn => {
  const logger = getLogger();

  // Field refs for scroll-to-error functionality
  const fieldRefs = useRef<Record<string, HTMLInputElement | HTMLElement | null>>({
    firstName: null,
    lastName: null,
    role: null,
    providerNumber: null,
    practiceName: null,
    practiceAddress: null,
    bio: null,
    photo: null
  });


  // Form state
  const [formData, setFormData] = useState<ProfileFormState['values']>({
    firstName: initialData.firstName || '',
    lastName: initialData.lastName || '',
    role: initialData.role || '',
    providerNumber: initialData.providerNumber || '',
    practiceName: initialData.practiceName || '',
    practiceAddress: initialData.practiceAddress || '',
    bio: initialData.bio || '',
    photo: initialData.photo || undefined,
    consent: {
      dataProcessing: initialData.consent?.dataProcessing ?? true,
      termsOfService: initialData.consent?.termsOfService ?? true,
      privacyPolicy: initialData.consent?.privacyPolicy ?? true,
      biometricProcessing: initialData.consent?.biometricProcessing ?? false,
    }
  });

  const validation = useProviderValidation();

  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [touchedFields, setTouchedFields] = useState<Set<string>>(new Set());

  // Update form data when initialData changes
  useEffect(() => {
    if (initialData) {
      setFormData(prev => ({
        ...prev,
        ...initialData,
        consent: {
          dataProcessing: initialData.consent?.dataProcessing ?? prev.consent.dataProcessing,
          termsOfService: initialData.consent?.termsOfService ?? prev.consent.termsOfService,
          privacyPolicy: initialData.consent?.privacyPolicy ?? prev.consent.privacyPolicy,
          biometricProcessing: initialData.consent?.biometricProcessing ?? prev.consent.biometricProcessing,
        }
      }));
    }
  }, [initialData]);

  // Helper function to get field value
  const getFieldValue = useCallback((fieldName: string): unknown => {
    const value = formData[fieldName as keyof ProfileFormState['values']];
    return value;
  }, [formData]);

  // Validation functions
  const validateField = useCallback((fieldName: string, value?: unknown) => {
    const fieldValue = value ?? getFieldValue(fieldName);

    const validationMap: Record<string, () => { isValid: boolean; error?: string }> = {
      'firstName': () => validation.validateName(fieldValue as string, 'First name'),
      'lastName': () => validation.validateName(fieldValue as string, 'Last name'),
      'role': () => validation.validateRole(fieldValue as string),
      'providerNumber': () => validation.validateProviderNumber(fieldValue as string, 'AU'),
      'practiceName': () => validation.validatePracticeName(fieldValue as string),
      'practiceAddress': () => validation.validatePracticeAddress(fieldValue as string),
      'bio': () => validation.validateBio(fieldValue as string),
    };

    const result = validationMap[fieldName]?.() || { isValid: true };

    setFieldErrors(prev => ({
      ...prev,
      [fieldName]: result.isValid ? '' : (result.error || ''),
    }));

    return result.isValid;
  }, [validation, getFieldValue]);

  /**
   * Get field validation props for FormField component
   */
  const getFieldValidation = useCallback((fieldName: string) => {
    const error = fieldErrors[fieldName];
    const isTouched = touchedFields.has(fieldName);
    const shouldShowError = isTouched && error;

    return {
      error: shouldShowError ? error : undefined,
      warning: undefined,
      success: undefined
    };
  }, [fieldErrors, touchedFields]);

  /**
   * Get provider number helper text based on selected country
   */
  const getProviderNumberHelperText = useCallback(() => {
    return 'Australian provider number format: 10 digits (e.g., 1234567890)';
  }, []);

  /**
   * Handle field value changes
   */
  const handleFieldChange = useCallback((fieldName: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));

    // Validate if field has been touched
    if (touchedFields.has(fieldName)) {
      validateField(fieldName, value);
    }
  }, [touchedFields, validateField]);


  /**
   * Handle photo field changes
   */
  const handlePhotoChange = useCallback((photo: File | undefined) => {
    setFormData(prev => ({
      ...prev,
      photo
    }));

    // Validate if field has been touched and photo is provided
    if (touchedFields.has('photo') && photo) {
      validateField('photo', photo);
    }
  }, [touchedFields, validateField]);

  /**
   * Handle field blur events
   */
  const handleFieldBlur = useCallback((fieldName: string) => {
    setTouchedFields(prev => new Set(Array.from(prev).concat(fieldName)));
    validateField(fieldName);
  }, [validateField]);

  /**
   * Handle form submission
   */
  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();

    if (disabled || isLoading) return;

    // Mark all fields as touched
    const allFields = [
      'firstName', 'lastName', 'role', 'providerNumber', 'practiceName',
      'practiceAddress', 'bio'
    ];
    setTouchedFields(new Set(allFields));

    // Validate all fields and collect results
    const validationResults: { field: string; isValid: boolean; error?: string }[] = [];
    const newFieldErrors: Record<string, string> = {};

    allFields.forEach(field => {
      const fieldValue = getFieldValue(field);
      const validationMap: Record<string, () => { isValid: boolean; error?: string }> = {
        'firstName': () => validation.validateName(fieldValue as string, 'First name'),
        'lastName': () => validation.validateName(fieldValue as string, 'Last name'),
        'role': () => validation.validateRole(fieldValue as string),
        'providerNumber': () => validation.validateProviderNumber(fieldValue as string, 'AU'),
        'practiceName': () => validation.validatePracticeName(fieldValue as string),
        'practiceAddress': () => validation.validatePracticeAddress(fieldValue as string),
        'bio': () => validation.validateBio(fieldValue as string),
      };

      const result = validationMap[field]?.() || { isValid: true };
      validationResults.push({ field, ...result });
      newFieldErrors[field] = result.isValid ? '' : (result.error || '');
    });


    // Update field errors state
    setFieldErrors(newFieldErrors);

    const isFormValid = validationResults.every(result => result.isValid);

    if (!isFormValid) {
      // Create immediate validation state for scroll-to-error using the actual validation results
      const immediateValidationState: Record<string, { touched?: boolean; result: { isValid: boolean; error?: string } }> = {};

      FORM_FIELDS.forEach(field => {
        const validationResult = validationResults.find(r => r.field === field);
        immediateValidationState[field] = {
          touched: true,
          result: {
            isValid: validationResult?.isValid ?? true,
            error: validationResult?.error
          }
        };
      });

      // Use immediate validation state for scrolling - increase timeout to ensure DOM updates
      setTimeout(() => {
        scrollToFirstErrorField(
          [...FORM_FIELDS],
          immediateValidationState,
          fieldRefs.current,
          {
            behavior: 'smooth',
            block: 'center',
            inline: 'nearest',
            focusDelay: 300,
            shouldFocus: true
          }
        );
      }, 100); // Timeout to account for validation state updates and DOM rendering

      return;
    }

    try {
      // Submit form
      await onSubmit(formData);
    } catch (error) {
      // Error handling is managed by parent component
      logger.error('Provider profile update form submission error:', error);
    }
  }, [disabled, isLoading, formData, validation, onSubmit, getFieldValue]);

  return {
    // Form state
    formData,
    fieldRefs,

    // Form fields configuration
    FORM_FIELDS,

    // Form validation state
    fieldErrors,
    touchedFields,


    // Utility functions
    getFieldValue,
    validateField,
    getFieldValidation,
    getProviderNumberHelperText,

    // Event handlers
    handleFieldChange,
    handlePhotoChange,
    handleFieldBlur,
    handleSubmit,
  };
};